import {
    type AddressFindResponseModel,
    type AddressModel,
    EventTypeEnum,
} from "@/models";
import { defineStore } from "pinia";
import { useCustomerStore, useDataLayerStore, useGlobalStore } from "@/stores";
import axios from "axios";

export const useAddressStore = defineStore("address", {
    state: (): AddressModel => {
        return {
            loadingAddress: false,
            manualAddress: false,
            addressList: [],
            formattedAddresses: [],
            selectedContainer: "",
            showAddress: false,
            showAddressDropdown: false,
        };
    },
    getters: {
        customer: () => {
            return useCustomerStore();
        },
        dataLayer: () => {
            return useDataLayerStore();
        },
        global: () => {
            return useGlobalStore();
        },
    },
    actions: {
        changeAddress(): void {
            this.customer.address.postcode = "";
            this.customer.address.line1 = "";
            this.customer.address.line2 = "";
            this.customer.address.line3 = "";
            this.customer.address.line4 = "";
            this.customer.address.line5 = "";
            this.addressList = [];
            this.formattedAddresses = [];
            this.customer.selectedAddress = "";
            this.showAddress = false;
        },
        triggerPostcodeLookup(): void {
            if (this.validPostcode()) {
                this.postcodeLookup();
                this.dataLayer.triggerEvent(
                    EventTypeEnum.QuestComplete,
                    "address.postcode"
                );
            }
        },
        validPostcode(postcodeInput?: string): boolean {
            const postcode = (
                postcodeInput ?? this.customer.address.postcode
            ).toUpperCase();

            const blockedPrefixes = [
                "AI",
                "ASCN",
                "BIQQ",
                "BBND",
                "BF",
                "FIQQ",
                "GX",
                "GY",
                "JE",
                "IM",
                "MSR",
                "PCRN",
                "SIQQ",
                "STHL",
                "TDCU",
                "VG",
                "GE",
                "XX",
            ];

            return !blockedPrefixes.some((prefix) =>
                postcode.startsWith(prefix)
            );
        },
        async postcodeLookup(): Promise<void> {
            this.loadingAddress = true;
            this.customer.selectedAddress = null;
            this.addressList = [];
            this.formattedAddresses = [];
            const lookupPostcode = this.customer.address.postcode
                .toUpperCase()
                .replace(/\s/g, "");

            try {
                const response = await axios.get<AddressFindResponseModel[]>(
                    `${this.global.env.API_URL}/api/v2/address/find/${lookupPostcode}`
                );

                if (response.status === 200) {
                    response.data.forEach(
                        (addressData: AddressFindResponseModel) => {
                            this.formattedAddresses.push({
                                label: addressData.text,
                                value: addressData.id,
                            });
                        }
                    );
                    this.addressList = response.data;
                    this.showAddressDropdown = true;
                    this.loadingAddress = false;
                    return;
                }
                this.showAddressDropdown = false;
                this.loadingAddress = false;
                this.manualAddress = true;
            } catch {
                this.showAddressDropdown = false;
                this.loadingAddress = false;
                this.manualAddress = true;
            }
        },
        async grabAddress(): Promise<void> {
            if (this.addressList.length === 1) {
                this.customer.selectedAddress = this.formattedAddresses[0]
                    .value as string;
            }

            try {
                const response = await axios.get(
                    `${this.global.env.API_URL}/api/v2/address/retrieve/${this.customer.selectedAddress}`
                );

                if (response.status === 200) {
                    this.manualAddress = false;
                    this.showAddressDropdown = false;
                    this.showAddress = true;
                    this.customer.address.line1 = response.data.line1;
                    this.customer.address.line2 = response.data.line2;
                    this.customer.address.line3 = response.data.line3;
                    this.customer.address.line4 = response.data.line4;
                    this.customer.address.line5 = response.data.line5;
                    this.customer.address.postcode = response.data.postcode;
                    this.dataLayer.triggerEvent(
                        EventTypeEnum.QuestComplete,
                        "selectedAddress"
                    );
                    return;
                }
                this.loadingAddress = false;
            } catch {
                this.showAddress = false;
                this.manualAddress = true;
                this.loadingAddress = false;
            }
        },
    },
});
